import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Background, Controls, Edge, Node, ReactFlow, useEdgesState, useNodesInitialized, useNodesState } from "@xyflow/react";

import type { ProcessDataMessage } from "fond/topologicalView/processData";
import ProcessDataWorker from "fond/topologicalView/processData?worker";
import { getFixedColumnsWidth } from "fond/topologicalView/spliceRangeColumns";
import SpliceRangeNode from "fond/topologicalView/SpliceRangeNode";
import { useTopologicalViewContext } from "fond/topologicalView/TopologicalViewContext";
import { TopologicalEdgeData, TopologicalNodeData } from "fond/topologicalView/types";

import "@xyflow/react/dist/style.css";

const nodeTypes = {
  spliceRange: SpliceRangeNode,
};

const fixedColumnsWidth = getFixedColumnsWidth();

/**
 * Topological view component.
 *
 * This view allows users to visualize the network topology based on splice tables.
 */
const TopologicalView: React.FC = () => {
  const {
    data: { spliceNodes },
  } = useTopologicalViewContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<TopologicalNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<TopologicalEdgeData>>([]);
  const nodesInitialized = useNodesInitialized();
  const workerRef = useRef<Worker | null>(null);

  // Process the API data into a more convenient form using a Web Worker.
  // This keeps the UI responsive when dealing with large designs.
  useEffect(() => {
    const worker = new ProcessDataWorker();

    worker.onmessage = (event) => {
      setNodes(event.data.nodes);
      setEdges(event.data.edges);
      setLoading(false);
    };

    workerRef.current = worker;

    // Clean up the worker when the component unmounts
    return () => {
      workerRef.current?.terminate();
    };
  }, [setNodes, setEdges]);

  // Process design nodes into the React Flow graph format
  useEffect(() => {
    if (spliceNodes && workerRef.current) {
      setLoading(true);
      const message: ProcessDataMessage = { spliceNodes, fixedColumnsWidth };
      workerRef.current.postMessage(message);
    }
  }, [spliceNodes]);

  return (
    <>
      {!nodesInitialized && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <CircularProgress sx={{ color: "white" }} />
        </Box>
      )}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodesConnectable={false}
          fitView
          defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
          minZoom={0.25}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      )}
    </>
  );
};

export default TopologicalView;
